'use client';

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/app/api/client';
import ConnectionModal from './components/ConnectionModal';
import DisconnectionModal from './components/DisconnectionModal';

import { ProfileSection } from '../profile/components';
import { Button } from '@/components/forms';
import { useSocialConnections } from '@/hooks/useSocialConnections';
import { useToast } from '@/hooks/useToast';
import { PLATFORM_INFO } from '@/constants/social-platforms';
import { ConnectionFlowModal } from '../../settings/components/ConnectionFlowModal';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const Loader2 = ({ className }: { className?: string }) => <span className={className}>⏳</span>;

// Types should be moved to a central types file
interface Connection {
  id: string;
  platformName: string;
  platformType: string;
  iconUrl: string;
}

interface ConnectionType {
  type: string;
  displayName: string;
  iconUrl: string;
  authType: 'oauth' | 'token';
  requiredSettings: { key: string; label: string; type: string; options?: string[] }[];
}

export default function ConnectionsPage() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | ConnectionType | null>(null);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<Connection | null>(null);

  // Social Connections State
  const { connections: socialConnections, loading: socialLoading, initiateOAuth, disconnect: disconnectSocial, logInterest, loading: socialActionLoading } = useSocialConnections();
  const { addToast } = useToast();
  const [disconnectingSocialId, setDisconnectingSocialId] = useState<string | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [isSocialConfirmOpen, setSocialConfirmOpen] = useState(false);
  const [socialToDisconnect, setSocialToDisconnect] = useState<string | null>(null);
  const [isConnectionFlowOpen, setConnectionFlowOpen] = useState(false);

  const handleSocialConnectClick = async (platformKey: string, platformInfo: any) => {
    if (platformInfo.status === 'coming_soon') {
      try {
        await logInterest(platformKey);
        addToast(`Thanks for your interest in ${platformInfo.name}! We've logged your request and will prioritize it.`, 'success');
      } catch (err) {
        addToast(`An error occurred while logging your interest. Please try again later.`, 'error');
      }
    } else {
      setConnectingPlatform(platformKey);
      setConnectionFlowOpen(true);
    }
  };

  const handleSocialDisconnect = (connectionId: string) => {
    setSocialToDisconnect(connectionId);
    setSocialConfirmOpen(true);
  };

  const confirmSocialDisconnect = async () => {
    if (!socialToDisconnect) return;

    try {
      setDisconnectingSocialId(socialToDisconnect);
      await disconnectSocial(socialToDisconnect, true);
    } catch (err) {
      console.error('Failed to disconnect:', err);
    } finally {
      setDisconnectingSocialId(null);
      setSocialConfirmOpen(false);
      setSocialToDisconnect(null);
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'oauth_success') {
        setConnectionFlowOpen(false);
      } else if (event.data.type === 'oauth_error') {
        setConnectionFlowOpen(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const platformsToConnect = Object.entries(PLATFORM_INFO).filter(
    ([key]) => !socialConnections.some(c => c.platform_type === key)
  );

  const fetchData = useCallback(async () => {
    try {
      console.log('[Connections] Fetching connection data...');
      setLoading(true);
      const [typesRes, connectionsRes] = await Promise.all([
        apiClient.get('/api/connections/types'),
        apiClient.get('/api/connections')
      ]);
      console.log('[Connections] Received connections:', connectionsRes.data);
      setConnectionTypes(typesRes.data || []);
      setConnections(connectionsRes.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch connection data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (item: Connection | ConnectionType) => {
    setEditingConnection(item);
    setIsModalOpen(true);
  };

  const nativePlatforms = connectionTypes.filter(ct => ct.type !== 'custom_http');
  const customConnections = connections.filter(c => c.platformType === 'custom_http');

  return (

    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900">Manage Connections</h1>
          <p className="text-gray-600 mt-2">Connect your accounts to publish your content across different platforms.</p>
        </div>

        <ProfileSection title="Native Platforms" description="Connect to platforms with built-in support.">
          {loading ? <p className="text-gray-500">Loading platforms...</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-5 mt-5 border-t">
              {nativePlatforms
                .sort((a, b) => {
                  // BlogSpot first, then others
                  if (a.type === 'blogspot') return -1;
                  if (b.type === 'blogspot') return 1;
                  return 0;
                })
                .map(platform => {
                  const existingConnection = connections.find(c => c.platformType === platform.type);
                  const isMedium = platform.type === 'medium';
                  return (
                    <div key={platform.type} className="flex items-center justify-between p-5 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow min-h-[100px]">
                      {/* Left side: Icon, Name, Status */}
                      <div className="flex items-center space-x-4 flex-1">
                        <img src={platform.iconUrl} alt={platform.displayName} className="w-12 h-12 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-base">{platform.displayName}</p>
                          {existingConnection ? (
                            <p className="text-sm text-green-600 mt-1 flex items-center">
                              <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                              Connected
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Not connected</p>
                          )}
                          {isMedium && (
                            <p className="text-xs text-amber-600 mt-1">⚠️ Requires existing integration token</p>
                          )}
                        </div>
                      </div>

                      {/* Right side: Buttons in column */}
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          onClick={() => openModal(existingConnection || platform)}
                          className="bg-blue-600 text-white hover:bg-blue-700 min-w-[110px]">
                          {existingConnection ? 'Manage' : 'Connect'}
                        </Button>
                        {existingConnection && (
                          <Button
                            plain
                            color="red"
                            onClick={() => {
                              setConnectionToDisconnect(existingConnection);
                              setIsDisconnectModalOpen(true);
                            }}
                            className="min-w-[110px]"
                          >
                            Disconnect
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </ProfileSection>

        <ProfileSection title="Social Sharing" description="Connect your social media accounts to share articles with one click.">
          {socialLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connected Accounts */}
              {socialConnections.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  {socialConnections.map(connection => {
                    const platform = PLATFORM_INFO[connection.platform_type as keyof typeof PLATFORM_INFO];
                    return (
                      <div
                        key={connection.id}
                        className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`${platform?.color} rounded-full w-10 h-10 flex items-center justify-center text-white text-lg`}>
                              {platform?.icon}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {connection.platform_name}
                              </p>
                              <p className="text-sm text-gray-600">
                                @{connection.platform_user_name}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleSocialDisconnect(connection.id)}
                            disabled={disconnectingSocialId === connection.id || socialActionLoading}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            title="Disconnect"
                          >
                            {disconnectingSocialId === connection.id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <TrashIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Connect New Accounts */}
              {platformsToConnect.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    {socialConnections.length > 0 ? 'Connect More Accounts' : 'Available Platforms'}
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {platformsToConnect.map(([platformKey, platformInfo]) => (
                      <button
                        key={platformKey}
                        onClick={() => handleSocialConnectClick(platformKey, platformInfo)}
                        disabled={connectingPlatform === platformKey || socialActionLoading}
                        className="relative rounded-lg border-2 border-dashed border-gray-300 bg-white p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`${platformInfo.color} rounded-full w-10 h-10 flex items-center justify-center text-white text-lg`}>
                            {platformInfo.icon}
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-semibold text-gray-900">
                              {connectingPlatform === platformKey ? (
                                <span className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Connecting...
                                </span>
                              ) : (
                                `Connect ${platformInfo.name}`
                              )}
                            </p>
                            {platformInfo.status === 'coming_soon' && (
                              <span className="absolute top-2 right-2 text-xs font-semibold bg-gray-200 text-gray-600 px-2 py-1 rounded-full">Coming Soon</span>
                            )}
                            <p className="text-sm text-gray-600">
                              {platformKey === 'reddit' && 'Post to subreddits'}
                              {platformKey === 'whatsapp' && 'Send via WhatsApp'}
                              {platformKey === 'linkedin' && 'Post to your profile'}
                              {platformKey === 'twitter' && 'Post to your timeline'}
                              {platformKey === 'facebook' && 'Post to your profile'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ProfileSection>

        <ProfileSection title="Custom Connections" description="Connect to any platform with a custom HTTP endpoint.">
          <div className="flex justify-end">
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700 shadow-sm flex items-center gap-2"
              onClick={() => openModal(connectionTypes.find(ct => ct.type === 'custom_http')!)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Custom Connection
            </Button>
          </div>
          <ul className="mt-5 space-y-4 border-t pt-5">
            {customConnections.map((conn) => (
              <li key={conn.id} className="flex items-center justify-between p-4 border rounded-lg bg-white shadow-sm">
                <div className="flex items-center space-x-4">
                  <img src={conn.iconUrl} alt={conn.platformName} className="w-10 h-10" />
                  <div>
                    <p className="font-semibold text-gray-800">{conn.platformName}</p>
                    <p className="text-sm text-gray-500">Custom HTTP</p>
                  </div>
                </div>
                <div className='flex gap-4'>
                  <Button plain onClick={() => openModal(conn)}>Edit</Button>
                  <Button plain color="red" onClick={async () => {
                    if (window.confirm('Are you sure you want to delete this connection?')) {
                      await apiClient.delete(`/api/connections/${conn.id}`);
                      fetchData();
                    }
                  }}>Delete</Button>
                </div>
              </li>
            ))}
          </ul>
          {!loading && customConnections.length === 0 && <p className="text-sm text-gray-500 mt-5 pt-5 border-t">No custom connections yet.</p>}
        </ProfileSection>
      </div>

      <ConnectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={() => {
          setIsModalOpen(false);
          fetchData();
        }}
        connectionData={editingConnection}
      />

      <DisconnectionModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onComplete={() => {
          setIsDisconnectModalOpen(false);
          setConnectionToDisconnect(null);
          fetchData();
        }}
        connection={connectionToDisconnect}
      />

      <ConfirmationModal
        isOpen={isSocialConfirmOpen}
        onClose={() => setSocialConfirmOpen(false)}
        onConfirm={confirmSocialDisconnect}
        title="Disconnect Social Account"
      >
        <p>Are you sure you want to disconnect this account? This action cannot be undone.</p>
      </ConfirmationModal>

      {
        connectingPlatform && (
          <ConnectionFlowModal
            isOpen={isConnectionFlowOpen}
            platformName={PLATFORM_INFO[connectingPlatform as keyof typeof PLATFORM_INFO].name}
            onClose={() => {
              setConnectionFlowOpen(false);
              setConnectingPlatform(null);
            }}
            initiateAuth={async () => {
              const { authorization_url } = await initiateOAuth(connectingPlatform);
              window.open(authorization_url, 'oauthWindow', 'width=600,height=700');
            }}
          />
        )
      }
    </div>
  );
}
